import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { RpcError } from '@/data/rpc';
import { isPermissionKey } from '@/data/permissions';
import { ALL_FIELD_KEYS, fieldLabelKey } from './fieldSpecs';
import { moneyCents } from './format';
import { fromDb } from './loadStatus';
import { describeStorageError } from './storageErrors';

/**
 * A failed RPC -> the copy a dispatcher reads (design 5). SQLSTATE first, then the message shape. The
 * raw message is never shown (it is English and written for developers); it goes to console.warn.
 *
 * `describeRpcError` is pure and table-tested; `useRpcErrorMessage` translates its answer, translating
 * `keyValues` (a permission label, an entity noun, a field label) before interpolating them.
 */

export interface ErrorDescription {
  /** Full i18n key, under `errors.` */
  key: string;
  /** Literal interpolation values. */
  values?: Record<string, string>;
  /** Interpolation values that are themselves i18n keys (translated first). */
  keyValues?: Record<string, string>;
  /** Interpolation values that are LISTS of i18n keys: each translated, then joined with ', ' (audit-b-docs). */
  keyLists?: Record<string, string[]>;
  /** The form field the error is about (snake_case field-spec key). */
  field?: string;
  /** The stop the error is about (load_create_full's `stop N: ...` prefix): rendered as `errors.onStop`. */
  stop?: number;
}

/**
 * The 23505 messages, verbatim from the live bodies: app.fleet_unique_message (0066, used by
 * carrier_create/update and truck_create/update) and broker_create/broker_update (0067).
 */
const DUPLICATE_PATTERNS: { message: string; key: string; field: string }[] = [
  { message: 'a carrier with this MC number already exists', key: 'errors.duplicateCarrierMc', field: 'mc_number' },
  { message: 'a carrier with this DOT number already exists', key: 'errors.duplicateCarrierDot', field: 'dot_number' },
  { message: 'a broker with this MC number already exists', key: 'errors.duplicateBrokerMc', field: 'mc_number' },
  { message: 'a broker with this DOT number already exists', key: 'errors.duplicateBrokerDot', field: 'dot_number' },
  { message: 'this carrier already has a truck with that unit number', key: 'errors.duplicateUnit', field: 'unit_number' },
  { message: 'a truck with this VIN already exists', key: 'errors.duplicateVin', field: 'vin' },
];

/**
 * spec factoring (0201): the factoring writers and the carrier link, verbatim. The legacy factoring_* keys are
 * refused by carrier_update since 0201: only a stale portal sends them, so "reload".
 */
function factoringError(code: string | undefined, message: string): ErrorDescription | null {
  if (code === 'P0002' && message === 'factoring company not found') return { key: 'factoring.errors.notFound', field: 'factoring_id' };
  if (code === '55000' && message === 'factoring company is not active') return { key: 'factoring.errors.inactive', field: 'factoring_id' };
  if (code === '23505' && message === 'a factoring company with this name already exists') return { key: 'factoring.errors.duplicate', field: 'name' };
  const assigned = code === '55000' ? /^factoring company is assigned to (\d+) carrier\(s\)$/.exec(message) : null;
  if (assigned) return { key: 'factoring.errors.assigned', values: { count: assigned[1] } };
  if (code === '22023' && message === 'noa_received_on cannot be in the future') return { key: 'factoring.errors.noaFuture', field: 'noa_received_on' };
  if (code === '22023' && message.startsWith('factoring_company is deprecated')) return { key: 'errors.outdated' };
  return null;
}

/** fleet-driver-pay (0281): the driver settlement writers' refusals, verbatim. Only codes 55000 and 22023. */
function driverSettlementError(code: string | undefined, message: string): ErrorDescription | null {
  if (code === '55000') {
    let m = /^enter the hours of load (.+) first$/.exec(message);
    if (m) return { key: 'driverSettlements.errors.needsHours', values: { load: m[1]! } };
    m = /^load (.+) fee is disputed; resolve it or move the load to next week$/.exec(message);
    if (m) return { key: 'driverSettlements.errors.feeDisputed', values: { load: m[1]! } };
    if (/^driver settlement \S+ is marked paid; unmark it first$/.test(message)) return { key: 'driverSettlements.errors.unmarkFirst' };
    m = /^driver settlement \S+ carries its balance; cancel (\S+) first$/.exec(message);
    if (m) return { key: 'driverSettlements.errors.carried', values: { number: m[1]! } };
    if (/^driver settlement \S+ of that week is final$/.test(message)) return { key: 'driverSettlements.errors.weekFinal' };
    if (/^driver settlement \S+ is \w+; only a draft can be (changed|finalized)$/.test(message)) return { key: 'driverSettlements.errors.notDraft' };
    if (/^load .+ is not paid by the hour$/.test(message)) return { key: 'driverSettlements.errors.notHourly' };
  }
  if (code === '22023' && /^only \S+ is still owed on .+; cannot take \S+$/.test(message)) return { key: 'driverSettlements.errors.overRemaining' };
  return null;
}

/**
 * audit-e-ops (0336..0347): facilities, route miles, typed accessorials, lumper reimbursement; verbatim. A stop's
 * facility refusal inside load_create_full arrives as `stop N: facility_id: ...` and is unwrapped by the 22023
 * branch below, which calls back here with the inner message.
 */
function opsBlockError(code: string | undefined, message: string): ErrorDescription | null {
  if (code === '23505' && message === 'a facility with this name already exists') return { key: 'facilities.errors.duplicate', field: 'name' };
  if (code === '22023' && message === 'facility_id: the facility does not exist or was retired') return { key: 'errors.facilityGone', field: 'facility_id' };
  if (code === '22023' && message === 'kind must be pickup, delivery or both') return { key: 'errors.outdated' };
  if (code === '55000' && message === 'route miles were requested less than a minute ago') return { key: 'errors.routeMilesThrottled' };
  if (code === '55000' && /^load \S+ has already earned a fee; its accessorials can no longer change$/.test(message)) return { key: 'errors.accessorialsLocked' };
  if (code === '55000' && /^a reimbursement follows the lumper of load \S+; change it on the load$/.test(message)) return { key: 'errors.reimbursementFollowsLoad' };
  if (code === '55000' && /^load \S+ is on final driver settlement \S+; cancel it first$/.test(message)) return { key: 'driverSettlements.errors.onFinal' };
  if (code === '22023') {
    const bucket = /^use the accessorials list for (detention|lumper|other)$/.exec(message);
    if (bucket) return { key: 'errors.useAccessorialsList', field: `${bucket[1]}_amount` };
    if (message === 'only a lumper can be paid by the driver') return { key: 'errors.onlyLumperDriverPaid', field: 'paid_by_driver' };
    if (message === 'note must be at most 500 characters') return { key: 'accessorials.errors.noteTooLong', field: 'note' };
    if (/^kind must be one of detention, /.test(message) || message === 'only a computed route carries miles') return { key: 'errors.outdated' };
  }
  return null;
}

/**
 * loadboard-unified (0356, 0357, 0360): the load board refusals, verbatim. The 404 / 409 come from
 * app.raise_pgrst, whose body code stays P0002 / 55000. `truck not found` is shared with the fleet writers and
 * keeps the generic copy (NOT_FOUND_RE); the role refusals (42501) keep the generic ones too.
 */
const LOAD_BOARD_ERRORS: Record<string, ErrorDescription> = {
  '55000 automatic search is turned off for this agency': { key: 'loadBoard.errors.autoOff' },
  'P0002 saved search not found': { key: 'loadBoard.errors.searchNotFound' },
  '55000 load board is turned off for this agency': { key: 'loadBoard.errors.boardOff' },
  '55000 no Truckstop Integration ID': { key: 'loadBoard.errors.noIntegrationId' },
  '55000 monthly Truckstop search limit reached': { key: 'loadBoard.errors.limitReached' },
  'P0002 results expired': { key: 'loadBoard.errors.expired' },
  '22023 truckstop_integration_id must be the digits of your Truckstop Integration ID (1 to 2147483647)': {
    key: 'loadBoard.errors.integrationId',
    field: 'truckstop_integration_id',
  },
  '22023 dest_city needs dest_state': { key: 'validation.stateRequired', field: 'dest_state' },
};

function loadBoardError(code: string | undefined, message: string): ErrorDescription | null {
  if (!code) return null;
  const exact = LOAD_BOARD_ERRORS[`${code} ${message}`];
  if (exact) return exact;
  if (code === '55000') {
    let m = /^at most (\d+) saved searches$/.exec(message);
    if (m) return { key: 'loadBoard.errors.tooManySaved', values: { count: m[1]! } };
    m = /^at most (\d+) automatic searches$/.exec(message);
    if (m) return { key: 'loadBoard.errors.tooManyAuto', values: { count: m[1]! } };
    m = /^board load already created as (\S+)$/.exec(message);
    if (m) return { key: 'loadBoard.errors.alreadyCreated', values: { load: m[1]! } };
  }
  return null;
}

/** The messages of the task writers (0116), verbatim. */
const TASK_ERRORS: Record<string, ErrorDescription> = {
  'task not found': { key: 'tasks.errors.notFound' },
  'task is canceled': { key: 'tasks.errors.canceled' },
  'title is required': { key: 'tasks.errors.titleRequired', field: 'title' },
  'title is longer than 200 characters': { key: 'tasks.errors.titleTooLong', field: 'title' },
  'detail is longer than 2000 characters': { key: 'tasks.errors.detailTooLong', field: 'detail' },
  'assignee is not an active member of this agency': { key: 'tasks.errors.assignee', field: 'assignee' },
  'load is not in your scope': { key: 'tasks.errors.outOfScope' },
  'carrier is not in your scope': { key: 'tasks.errors.outOfScope' },
  'driver is not in your scope': { key: 'tasks.errors.outOfScope' },
  'carrier does not match the load': { key: 'tasks.errors.carrierMismatch' },
  'title and detail of a system task are not editable': { key: 'tasks.errors.systemTitle' },
  'not a member of this agency': { key: 'errors.forbidden' },
};

/**
 * agency-billing (0166): the portal billing RPCs, verbatim, keyed `<code> <message>`. The 409s come from
 * app.raise_pgrst, whose body code stays 55000. `billing:*` are the portal's own failures while it waits
 * for a Stripe page (data/useBilling `requestBillingPage`).
 */
const BILLING_ERRORS: Record<string, ErrorDescription> = {
  '42501 not allowed: billing': { key: 'billing.errors.notAllowed' },
  '55000 subscribing from the portal is not enabled': { key: 'billing.errors.checkoutOff' },
  '55000 this agency is not charged': { key: 'billing.errors.notCharged' },
  '55000 this agency already has a subscription': { key: 'billing.errors.alreadySubscribed' },
  '55000 subscriptions are not open yet': { key: 'billing.errors.notOpenYet' },
  '55000 a checkout is already being prepared': { key: 'billing.errors.checkoutPending' },
  '55000 the billing portal is not enabled': { key: 'billing.errors.portalOff' },
  '55000 this agency has no billing account with our payment processor yet': { key: 'billing.errors.noCustomer' },
  '55000 self sign-up is not open': { key: 'billing.errors.selfSignupOff' },
  '42501 a verified email is required to create an agency': { key: 'billing.errors.verifiedEmail' },
  '55000 you already created an agency': { key: 'billing.errors.alreadyCreated' },
  // self-signup (0180): no single-use Turnstile pass for this token, and the platform-wide daily cap.
  '55000 captcha required': { key: 'billing.errors.captchaRequired' },
  '55000 self sign-up daily limit reached': { key: 'billing.errors.dailyLimit' },
  '22023 time_zone is required': { key: 'billing.errors.timeZone', field: 'time_zone' },
  '22023 load_number_prefix must be 2 to 5 letters or digits': { key: 'billing.errors.prefix', field: 'load_number_prefix' },
};

/**
 * reports-v2 (0181): the saved explore views, verbatim, keyed `<code> <message>`. The not-found is raised
 * through app.raise_pgrst (HTTP 404, body code P0002): another person's view and a view deleted meanwhile
 * answer alike. The copy lives in the explorer's lazy locale (`reports.explore.errors.*`), loaded with the
 * only screen that calls these RPCs.
 */
const REPORT_VIEW_ERRORS: Record<string, ErrorDescription> = {
  'P0002 report view not found': { key: 'reports.explore.errors.viewNotFound' },
  '22023 a view name has 1 to 80 characters': { key: 'reports.explore.errors.nameLength', field: 'name' },
  '22023 a view config is at most 32 KB': { key: 'reports.explore.errors.tooBig' },
  '22023 a view config is a JSON object': { key: 'errors.outdated' },
  '42501 only an admin or a manager shares a view': { key: 'reports.explore.errors.shareRole' },
};

function billingError(code: string | undefined, message: string): ErrorDescription | null {
  if (code === 'billing:failed') return { key: 'billing.errors.requestFailed' };
  if (code === 'billing:timeout') return { key: 'billing.errors.requestTimeout' };
  // self-signup: coreway-billing's answer to POST /billing/captcha (lib/turnstile verifyCaptcha).
  if (code === 'billing:captcha_failed') return { key: 'billing.errors.captchaFailed' };
  if (code === 'billing:captcha_unavailable') return { key: 'billing.errors.captchaUnavailable' };
  return code ? (BILLING_ERRORS[`${code} ${message}`] ?? null) : null;
}

/** P0002 `<noun> not found` (0064..0068; invoice, payment, receipt, load 0070..0073; member, invite 0077). */
const NOT_FOUND_RE = /^(agency|carrier|owner|truck|driver|broker|seat|invoice|payment|receipt|load|member|invite) not found$/;
/** 55000 `<noun> is not active` (0066, 0068; member 0077). */
const NOT_ACTIVE_RE = /^(carrier|truck|driver|owner|member) is not active$/;

/**
 * audit-a-dispatch (0301): load_dispatch refused on the agency's dispatch rules (block), with the axis tokens
 * only, comma separated: `dispatch blocked: driver_busy, truck_docs`. Busy only, documents / setup only, or
 * both (an unknown token reads as "both": a refusal by the rules, never a generic state).
 */
function dispatchBlocked(message: string): ErrorDescription | null {
  const m = /^dispatch blocked: (.+)$/.exec(message);
  if (!m) return null;
  const tokens = m[1]!.split(',').map((x) => x.trim()).filter(Boolean);
  const busy = tokens.every((x) => x === 'driver_busy' || x === 'truck_busy');
  const docs = tokens.every((x) => x === 'driver_docs' || x === 'truck_docs' || x === 'broker_setup');
  if (busy) return { key: 'loadActions.dispatchDialog.blockedBusy' };
  if (docs) return { key: 'loadActions.dispatchDialog.blockedDocs' };
  return { key: 'loadActions.dispatchDialog.blocked' };
}

/**
 * 55000 of the team (0077) and the two ledgers (0070..0073), verbatim. A pattern is a string (exact) or
 * a RegExp (the message carries an id or a status).
 */
const STATE_PATTERNS: { match: string | RegExp; key: string }[] = [
  // team, 0077
  { match: 'an agency needs at least one active admin', key: 'errors.lastAdmin' },
  { match: 'this person is already a member of the agency', key: 'errors.alreadyMember' },
  { match: 'this person is an inactive member; reactivate them instead', key: 'errors.inactiveMember' },
  { match: 'invite is no longer pending', key: 'errors.inviteNotPending' },
  { match: 'you are already a member of this agency', key: 'errors.alreadyJoined' },
  { match: 'your membership is inactive; ask an admin to reactivate you', key: 'errors.membershipInactive' },
  // carrier invoices, 0070 (invoice_payment_record, invoice_write_off, invoice_payment_reverse, invoice_void)
  { match: /^invoice is a draft; send it before /, key: 'errors.invoiceDraft' },
  { match: 'invoice is already paid', key: 'errors.invoicePaid' },
  { match: 'invoice is canceled', key: 'errors.invoiceCanceled' },
  { match: /^invoice \S+ is already voided$/, key: 'errors.invoiceCanceled' },
  { match: 'nothing to write off', key: 'errors.nothingToWriteOff' },
  { match: 'payment is already reversed', key: 'errors.alreadyReversed' },
  { match: 'receipt is already reversed', key: 'errors.alreadyReversed' },
  { match: /^invoice \S+ has payments; reverse them before voiding$/, key: 'errors.invoiceHasPayments' },
  // broker receivables, 0071..0073 (and load_cancel)
  { match: 'load is already invoiced to the broker', key: 'errors.alreadyInvoiced' },
  { match: /^load is \S+; only a load with its POD received can be invoiced to the broker$/, key: 'errors.notPodReceived' },
  { match: 'load is not invoiced to the broker', key: 'errors.notInvoiced' },
  { match: /^receivable is (already )?closed(; reopen it first)?$/, key: 'errors.receivableClosed' },
  { match: 'receivable is open', key: 'errors.receivableOpen' },
  { match: 'reverse the receipts before unmarking', key: 'errors.reverseReceiptsFirst' },
  { match: /^load \S+ is invoiced to the broker; unmark it before cancelling$/, key: 'errors.unmarkBeforeCancel' },
  // load_update (0079): the broker invoice freezes the broker and the number; a closed load is not edited.
  { match: /^load \S+ is invoiced to the broker; unmark it before changing the broker or the load number$/, key: 'errors.unmarkBeforeEdit' },
  { match: /^load \S+ is (canceled|paid); it cannot be edited$/, key: 'errors.loadNotEditable' },
];

/**
 * The load RPCs (portal-loads design 7), verbatim from the live bodies at 0077 (`pg_get_functiondef`,
 * public `load_*` and app `run_load_context`, `dispatch_answer_context`, `stamp_context`,
 * `load_transition`, `accrue_dispatch_fee`). Ids and timestamps inside the messages are matched loosely.
 */
const RUN_REFUSALS = [
  /^not allowed to create loads for carrier /,
  /^not allowed to run loads for carrier /,
  /^not allowed to advance load /,
  /^not allowed to answer the dispatch on load /,
  /^not allowed to stamp load /,
  /^not allowed to add \S+ to load /,
  /^only the office marks a POD received/,
];

const LOAD_STATE_PATTERNS: { match: RegExp; key: string }[] = [
  { match: /^load \S+ is \S+; an itinerary for a finished load is noise$/, key: 'errors.loadFinished' },
  { match: /^stop \d+ of load \S+ was already visited$/, key: 'errors.stopVisited' },
  { match: /^stop \d+ of load \S+ was already arrived at /, key: 'errors.stopAlreadyArrived' },
  { match: /^stop \d+ of load \S+ was already departed at /, key: 'errors.stopAlreadyDeparted' },
  { match: /^stop \d+ of load \S+ has no arrival to depart from$/, key: 'errors.stopNotArrived' },
  { match: /; removing it would leave the driver with no itinerary$/, key: 'errors.lastStopOfKind' },
  { match: /^load \S+ is billed on invoice .*; remove the line before cancelling$/, key: 'errors.feeOnInvoice' },
  { match: /^load \S+ is billed on invoice .*, which is no longer a draft$/, key: 'errors.feeInvoiceSent' },
  // load_unassign (0079): a stop was stamped, so it is a mid-route swap, not a take-back.
  { match: /^load \S+ is already rolling \(a stop was arrived at\); cancel it or swap the driver instead$/, key: 'errors.unassignRolling' },
  // load_stop_restamp (audit-c 0317): the time was never recorded, so there is nothing to correct.
  { match: /^stop \d+ of load \S+ has no (arrival|departure) to correct$/, key: 'errors.noStampToCorrect' },
];

/**
 * 55000 of the owner layer (0096/0097, change spec owner-fees), verbatim from the live bodies. Ids,
 * numbers and dates inside the messages are matched loosely. The copy lives in `ownerFees.errors.*`.
 */
const OWNER_FEE_STATE_PATTERNS: { match: string | RegExp; key: string }[] = [
  { match: /^set the truck's owner first/, key: 'ownerFees.errors.truckNoOwner' },
  { match: /^the owner of truck .* is not active$/, key: 'ownerFees.errors.ownerInactive' },
  { match: /^truck .*already has a lease on those dates$/, key: 'ownerFees.errors.overlap' },
  { match: /^a load of this lease was delivered (before|after) /, key: 'ownerFees.errors.loadsOutside' },
  // 0099: the statement number is named to admin and accounting only.
  { match: /^load \S+ is on final owner statement( \S+)?; cancel it first$/, key: 'ownerFees.errors.onFinalStatement' },
  { match: /^owner statement \S+ is (final|canceled); only a draft can be changed$/, key: 'ownerFees.errors.notDraft' },
  { match: /^load \S+ is disputed; remove it or clear the dispute$/, key: 'ownerFees.errors.disputedOnFinalize' },
  { match: /^owner statement \S+ carries this balance; cancel \S+ first$/, key: 'ownerFees.errors.cancelOrder' },
  { match: /^the owner statement of the week of \S+ is final; cancel it first$/, key: 'ownerFees.errors.weekFinal' },
  { match: /^owner statement \S+ is already canceled$/, key: 'ownerFees.errors.alreadyCanceled' },
  { match: /^(that line is already waived|only a waived line can be restored)$/, key: 'ownerFees.errors.lineState' },
  // fee-overrides (0138/0139).
  { match: /^a TONU applies to a load whose truck was ordered and not loaded; /, key: 'errors.tonuState' },
  { match: /^load \S+ is not on a leased truck$/, key: 'ownerFees.errors.notLeased' },
  { match: 'restore the line before changing its amount', key: 'ownerFees.errors.lineWaivedAmount' },
];

/** 22023 of fee-overrides (0137..0139), verbatim from the live bodies: the load money and the owner edits. */
function feeOverrideParameter(message: string): ErrorDescription | null {
  if (/^a TONU is recorded (when the load is canceled|only by load_cancel)/.test(message)) return { key: 'errors.tonuOnLiveLoad' };
  if (/^the TONU of load \S+ cannot be repriced to 0;/.test(message)) return { key: 'errors.tonuNotZero', field: 'tonu_amount' };
  if (/^a TONU is between 0\.01 and /.test(message)) return { key: 'errors.invalidAmount', field: 'tonu_amount' };
  const money = /^(linehaul|tonu|detention|lumper|other)_amount must be between 0 and /.exec(message);
  if (money) return { key: 'errors.invalidAmount', field: money[1] === 'linehaul' ? 'linehaul' : `${money[1]}_amount` };
  if (/^accessorial_amount \S+ is below the itemized TONU, detention and lumper/.test(message)) return { key: 'errors.extrasBelowParts' };
  if (message === 'the commission is between 0 and 100 with at most two decimals') return { key: 'ownerFees.errors.commissionPct', field: 'pct' };
  if (message === 'a reason takes at most 500 characters') return { key: 'errors.reasonTooLong', field: 'reason' };
  if (message === 'a load line follows its commission; a balance forward can only be waived') return { key: 'ownerFees.errors.lineNotEditable' };
  // Portal bugs (the UI never sends these): logged, never shown raw.
  if (
    / is linehaul_tonu \(linehaul plus TONU\);/.test(message) ||
    message === 'send accessorial_amount or the itemized amounts, not both' ||
    message === 'tonu_driver_pay must be plan or none'
  ) {
    return { key: 'errors.outdated' };
  }
  return null;
}

/**
 * 55000 of broker credit (0132, change spec 2026-09-24/credit), verbatim from the live bodies. The copy
 * lives in `credit.errors.*`. The gate's message carries the flags in parentheses.
 */
const CREDIT_STATE_PATTERNS: { match: string | RegExp; key: string }[] = [
  { match: /^broker credit blocks this load \(/, key: 'credit.errors.blocked' },
  { match: 'incident is already resolved', key: 'credit.errors.alreadyResolved' },
  { match: 'incident is not resolved', key: 'credit.errors.notResolved' },
  { match: /^this short pay belongs to the broker ledger/, key: 'credit.errors.ledgerShortPay' },
];

/** 22023 of broker credit (0132) that are not a plain `<field> ...` shape. */
function creditParameter(message: string): ErrorDescription | null {
  const m = /^(checked_at|occurred_on|resolved_on) cannot be in the future$/.exec(message);
  if (m) return { key: 'errors.dateInFuture', field: m[1] };
  if (message === 'checked_at cannot be more than a year ago') return { key: 'credit.errors.checkTooOld', field: 'checked_at' };
  if (message === 'resolved_on cannot be before the incident') return { key: 'credit.errors.resolvedBefore', field: 'resolved_on' };
  if (message === 'the load is not on this broker') return { key: 'credit.errors.loadNotOnBroker' };
  if (message === 'reason is required') return { key: 'errors.reasonRequired', field: 'reason' };
  if (/^(source|decision|kind) must be one of /.test(message) || message === 'carrier_id and load_id must be ids') {
    return { key: 'errors.outdated' };
  }
  return null;
}

/** 55000 `illegal transition: load <id> is <status>, expected one of {...}`: the status it is NOW. */
function loadMoved(message: string): ErrorDescription | null {
  const m = /^illegal transition: load \S+ is (\S+), expected one of /.exec(message);
  if (!m) return null;
  const status = fromDb(m[1]);
  return { key: 'errors.loadMoved', keyValues: { status: `loadStatus.${status ?? 'unknown'}` } };
}

/** 22023 of the load RPCs (null when the message is none of them). */
function loadParameter(message: string): ErrorDescription | null {
  if (/^load \S+ needs at least one pickup and one delivery before it can be dispatched$/.test(message)) return { key: 'errors.needsStops' };
  if (/^driver \S+ does not belong to carrier /.test(message)) return { key: 'errors.driverNotSeated', field: 'driver' };
  if (/^truck \S+ does not belong to carrier /.test(message)) return { key: 'errors.truckNotOfCarrier', field: 'truck' };
  if (/^load \S+ cannot earn a fee without a linehaul amount and a percentage$/.test(message)) return { key: 'errors.noFeePlan' };
  if (/^load \S+ has already earned a fee; use load_reprice$/.test(message)) return { key: 'errors.useReprice' };
  if (/^load \S+ cannot be repriced without a linehaul amount$/.test(message)) return { key: 'errors.invalidAmount', field: 'linehaul' };
  if (/^a departure of .* precedes the arrival at /.test(message)) return { key: 'errors.stampBeforeArrival', field: 'at' };
  if (/^a stamp of .* is in the future$/.test(message)) return { key: 'errors.dateInFuture', field: 'at' };
  if (/^stop \d+ of load \S+ has no arrival to depart from$/.test(message)) return { key: 'errors.stopNotArrived' };
  // load_stop_restamp (audit-c 0317): the corrected arrival would follow the recorded departure.
  if (/^an arrival of .* follows the departure at /.test(message)) return { key: 'errors.arrivalAfterDeparture', field: 'at' };
  if (message === 'reason must be at most 500 characters') return { key: 'errors.reasonTooLong', field: 'reason' };
  // load_unassign (0079): an accepted load needs a reason to be taken back.
  if (/^load \S+ was accepted by its driver; taking it back needs a reason$/.test(message)) {
    return { key: 'errors.unassignNeedsReason', field: 'reason' };
  }
  // load_stop_set / load_create_full (0079, 0087): the appointment window.
  if (message === 'appointment_end_at needs an appointment_at') return { key: 'errors.windowNeedsStart', field: 'appointment_end_at' };
  if (message === 'appointment_end_at cannot be before appointment_at') {
    return { key: 'errors.windowBeforeStart', field: 'appointment_end_at' };
  }
  // Portal bugs (a wrong path, a wrong step, a wrong field name): logged, never shown raw.
  if (
    /^storage_path .* does not belong to load /.test(message) ||
    /^\S+ is not a step on the road; it has its own rpc$/.test(message) ||
    // audit-c (0316, 0317): a time on a step that takes none, a shortcut to anything but delivered, a bad kind.
    /^a time is only taken for at_pickup and delivered, not \S+$/.test(message) ||
    message === 'load_advance_to only goes to delivered' ||
    message === 'kind must be arrive or depart' ||
    message === 'a corrected time is required' ||
    /^\S+ is not a clearable field of a stop$/.test(message) ||
    message === 'no fields named' ||
    message === 'time_zone is not a known time zone'
  ) {
    return { key: 'errors.outdated' };
  }
  return null;
}

/** 54000 invite send limits (app.member_invite_send_refusal, 0075). */
const LIMIT_PATTERNS: { match: string | RegExp; key: string }[] = [
  { match: 'invite email limit reached for today; try again later', key: 'errors.inviteDailyLimit' },
  { match: /^this invite was already sent \d+ times; revoke it and invite again$/, key: 'errors.inviteSendLimit' },
  { match: 'wait a minute before sending this invite again', key: 'errors.inviteCooldown' },
];

function findPattern(patterns: { match: string | RegExp; key: string }[], message: string): string | null {
  const hit = patterns.find((p) => (typeof p.match === 'string' ? p.match === message : p.match.test(message)));
  return hit ? hit.key : null;
}

/** A numeric in a server message (`790.00`, `1240`) as the portal's money text. */
function moneyOf(raw: string): string {
  const n = Number(raw);
  return Number.isFinite(n) ? moneyCents(n) : raw;
}

/** 22023 of the ledgers and the team (0070..0077) that are not a plain `<field> ...` shape. */
function ledgerParameter(message: string): ErrorDescription | null {
  let m = /^amount exceeds the balance of (\S+)$/.exec(message);
  if (m) return { key: 'errors.amountOverBalance', values: { balance: moneyOf(m[1]) }, field: 'amount' };
  m = /^amount cannot be below what was already received \((\S+)\)$/.exec(message);
  if (m) return { key: 'errors.amountBelowReceived', values: { received: moneyOf(m[1]) }, field: 'amount' };
  if (/^amount (must |is required$)/.test(message)) return { key: 'errors.invalidAmount', field: 'amount' };
  m = /^(paid_on|invoiced_on) cannot be in the future$/.exec(message);
  if (m) return { key: 'errors.dateInFuture', field: m[1] };
  if (message === 'paid_on cannot be before the invoice date' || message === 'paid_on cannot be before invoiced_on') {
    return { key: 'errors.dateBeforeInvoice', field: 'paid_on' };
  }
  if (message === 'due_on cannot be before invoiced_on') return { key: 'errors.dueBeforeInvoice', field: 'due_on' };
  if (/^an? (write-off|reversal|reopen|dispute|short pay|unmark) needs a reason$/.test(message)) {
    return { key: 'errors.reasonRequired', field: 'reason' };
  }
  if (/^(reason|short_reason) must be at most \d+ characters$/.test(message)) return { key: 'errors.reasonTooLong', field: 'reason' };
  if (message === 'factoring_company is required when the load is factored') {
    return { key: 'errors.factoringRequired', field: 'factoring_company' };
  }
  if (/^method must be one of /.test(message) || message === 'payer must be broker or factoring') return { key: 'errors.outdated' };
  // app.norm_email names the field first: `email is not a valid email address` (the invite dialog).
  if (message === 'email is not a valid email address') return { key: 'errors.invalidEmail', field: 'email' };
  if (message === 'only a dispatcher is assigned carriers') return { key: 'errors.onlyDispatcherCarriers' };
  return null;
}

/**
 * documents (0126, 0127): the carrier, truck and driver document writers. Checked before the switch so the
 * generic 22023 / 42501 / P0002 / 55000 branches keep their meaning for everything else.
 */
function documentError(code: string | undefined, message: string): ErrorDescription | null {
  // Load documents, replace / archive (0156).
  if (code === '42501' && /^not allowed to change load document /.test(message)) return { key: 'loadDocs.errors.notAllowed' };
  if (code === '55000' && /^load document \S+ was already replaced or archived$/.test(message)) return { key: 'loadDocs.errors.alreadyChanged' };
  if (code === '22023' && message === 'archiving a load document needs a reason') return { key: 'loadDocs.errors.reasonRequired', field: 'reason' };
  if (code === '22023' && message === 'the reason is longer than 500 characters') return { key: 'loadDocs.errors.reasonTooLong', field: 'reason' };
  if (code === '22023' && message === 'the new copy is already on the load') return { key: 'loadDocs.errors.alreadyOnLoad' }; // 0158
  if (code === '22023' && (/^the new copy of document \S+ is the same file$/.test(message) || /^document \S+ is not a load document$/.test(message))) {
    return { key: 'errors.outdated' };
  }
  if (code === '22023' && /^expires on \S+ is before issued on /.test(message)) return { key: 'docCenter.errors.expiryBeforeIssue', field: 'expires_on' };
  if (code === '22023' && /^a \S+ is not a (carrier|truck|driver) document$/.test(message)) return { key: 'docCenter.errors.wrongKind' };
  if (code === '55000' && /^document \S+ was (already )?replaced or archived$/.test(message)) return { key: 'docCenter.errors.alreadyReplaced' };
  if (code === '42501' && /^not allowed to (add \S+ to (carrier|truck|driver) |change document )/.test(message)) return { key: 'docCenter.errors.notAllowed' };
  if (code === '42501' && /^only an admin of agency \S+ edits its document requirements$/.test(message)) return { key: 'errors.adminOnly' };
  const gone = code === 'P0002' ? /^(truck|driver|document) \S+ does not exist$/.exec(message) : null;
  if (gone) return { key: 'errors.notFound', keyValues: { thing: `entities.${gone[1]}` } };
  return null;
}

/**
 * invoice-pdf (0145, 0146; design section 8): the document, numbering and delivery RPCs, verbatim; and the
 * two portal codes of the download (`document:failed`, `document:timeout`, data/useDocuments).
 */
function invoiceDocumentError(code: string | undefined, message: string): ErrorDescription | null {
  if (code === 'document:failed') return { key: 'documents.failed' };
  if (code === 'document:timeout') return { key: 'documents.timeout' };
  if (code === '54000' && /^the load documents are larger than 30 MB together/.test(message)) return { key: 'documents.errors.packetTooBig' };
  if (code === '55000') {
    const known: Record<string, string> = {
      'the carrier has no billing or contact email; add one or send without email': 'invoices.errors.noRecipient',
      'this document was emailed a moment ago; try again in a minute': 'documents.errors.throttleMinute',
      'this document was emailed 10 times today': 'documents.errors.throttleDay',
      'only a sent invoice can be emailed': 'documents.errors.notSent',
      'mark the load invoiced first': 'documents.errors.notInvoiced',
      'too many documents are being prepared; try again in a minute': 'documents.tooMany',
      'the broker invoice numbers of this carrier are exhausted; set a new prefix': 'receivables.errors.numbersExhausted',
      // F40 (0150): invoice_raise with no unbilled, undisputed fee refuses before minting a number.
      'nothing to bill for this carrier: no unbilled, undisputed dispatch fee': 'invoices.errors.nothingToBill',
    };
    if (known[message]) return { key: known[message] };
    // 0151: a charge-only invoice for a carrier that already has a draft.
    const draft = /^this carrier already has draft invoice (.+); add the charge to it$/.exec(message);
    if (draft) return { key: 'invoices.errors.hasDraft', values: { number: draft[1] } };
    return null;
  }
  if (code === '22023') {
    // 0151: invoice_raise_charge validates its charge before writing anything.
    if (message === 'a charge needs an amount above 0 with at most 2 decimals') return { key: 'errors.invalidField', keyValues: { field: 'invoices.charge.amountLabel' }, field: 'amount' };
    if (message === 'a charge needs a description of 1 to 200 characters') return { key: 'errors.invalidField', keyValues: { field: 'invoices.charge.descriptionLabel' }, field: 'description' };
    if (message === 'type the email address to send it to') return { key: 'documents.errors.typeEmail', field: 'to' };
    if (message === 'the broker has no email on file; type one') return { key: 'documents.errors.brokerNoEmail', field: 'to' };
    if (message === 'to is not a valid email address') return { key: 'errors.invalidEmail', field: 'to' };
    if (message === 'the prefix is 1 to 10 letters or digits') return { key: 'validation.prefix', field: 'broker_invoice_prefix' };
    if (/^upload the logo first/.test(message)) return { key: 'company.logo.uploadFirst', field: 'logo_path' };
    // Portal bugs (the UI never sends these): logged, never shown raw.
    if (
      // (`locale must be en or es` is left to the generic field rule: profile_update names the UI locale.)
      message === 'kind must be broker_invoice or broker_packet' ||
      message === 'attach must be packet or invoice' ||
      message === 'profile must be a JSON object'
    ) {
      return { key: 'errors.outdated' };
    }
  }
  return null;
}

export function describeRpcError(err: RpcError | { code?: string; message?: string }): ErrorDescription {
  const code = err.code || undefined;
  const message = (err.message ?? '').trim();

  // A Storage failure (data/useLoadDocuments): `storage:<statusCode>` goes to the storage map.
  if (code?.startsWith('storage:')) return describeStorageError({ statusCode: code.slice('storage:'.length), message });
  if (!code && /Failed to fetch|NetworkError|Load failed|fetch failed/i.test(message)) return { key: 'errors.network' };
  const docErr = documentError(code, message);
  if (docErr) return docErr;
  const invoiceDoc = invoiceDocumentError(code, message);
  if (invoiceDoc) return invoiceDoc;
  if (code === 'PGRST301' || code === 'PGRST303' || /JWT expired/i.test(message)) return { key: 'errors.sessionExpired' };

  // The task writers (0116, r3-tasks): exact messages, their own copy under tasks.errors.
  const task = TASK_ERRORS[message];
  if (task && code && ['22023', '42501', 'P0002', '55000'].includes(code)) return task;
  const factoring = factoringError(code, message);
  if (factoring) return factoring;
  const loadBoard = loadBoardError(code, message);
  if (loadBoard) return loadBoard;
  const ops = opsBlockError(code, message);
  if (ops) return ops;
  const settlement = driverSettlementError(code, message);
  if (settlement) return settlement;
  const billing = billingError(code, message);
  if (billing) return billing;
  const reportView = code ? REPORT_VIEW_ERRORS[`${code} ${message}`] : undefined;
  if (reportView) return reportView;

  switch (code) {
    case '42501': {
      if (message === 'no signed-in user') return { key: 'errors.sessionExpired' };
      if (message === 'confirm your email address first') return { key: 'errors.confirmEmail' };
      if (message === 'only an admin can grant this role') return { key: 'errors.onlyAdminGrants' };
      if (message === 'only an admin can change this member') return { key: 'errors.onlyAdminChanges' };
      if (/^only an admin may void invoice /.test(message)) return { key: 'errors.adminOnly' };
      const m = /^not allowed: (\S+)$/.exec(message);
      if (m && isPermissionKey(m[1])) {
        return { key: 'errors.notAllowed', keyValues: { action: `permissions.${m[1]}.label` } };
      }
      if (message === 'outside your carriers') return { key: 'errors.outsideScope' };
      if (message === 'driver also drives for carriers you do not run') return { key: 'errors.driverOutsideScope' };
      if (message === 'driver is on a truck outside your carriers') return { key: 'errors.driverOnTruckOutsideScope' };
      if (message.startsWith('only the agency admin')) return { key: 'errors.adminOnly' };
      if (RUN_REFUSALS.some((re) => re.test(message))) return { key: 'errors.notAllowedRun' };
      return { key: 'errors.forbidden' };
    }
    case '28000':
      // member_invite_accept / _decline: a password session must prove the mailbox with a sign-in link.
      return { key: 'errors.mailboxProof' };
    case '54000':
      return { key: findPattern(LIMIT_PATTERNS, message) ?? 'errors.limitReached' };
    case 'P0002': {
      // One answer for absent, another address, expired or revoked (0077): never guessed apart.
      if (message === 'invite not found or expired') return { key: 'errors.inviteNotFound' };
      const gone = /^(load|carrier) \S+ does not exist$/.exec(message);
      if (gone) return { key: 'errors.notFound', keyValues: { thing: `entities.${gone[1]}` } };
      if (/^load \S+ has no stop \d+$/.test(message)) return { key: 'errors.stopGone' };
      if (/^object .* was not uploaded$/.test(message)) return { key: 'errors.uploadMissing' };
      if (message === 'incident not found') return { key: 'credit.errors.incidentNotFound' };
      const m = NOT_FOUND_RE.exec(message);
      return m ? { key: 'errors.notFound', keyValues: { thing: `entities.${m[1]}` } } : { key: 'errors.notFoundGeneric' };
    }
    case '23503':
      return { key: 'errors.notFoundGeneric' };
    case '22023': {
      // load_create_full validates every stop first and prefixes `stop N: `: the inner message, on that stop.
      const onStop = /^stop (\d+): (.+)$/s.exec(message);
      if (onStop) {
        const { field: _field, ...inner } = describeRpcError({ code, message: onStop[2] });
        return { ...inner, stop: Number(onStop[1]) };
      }
      if (message === 'driver does not hold a seat on this carrier') return { key: 'errors.noSeat' };
      if (message === 'the admin role always has every permission') return { key: 'errors.adminAlways' };
      if (message === 'setup_date cannot be after today') return { key: 'errors.dateInFuture', field: 'setup_date' };
      if (/^unknown (field|setting|permission) /.test(message)) return { key: 'errors.outdated' };
      // audit-c (0318): the agency's free time at a stop.
      if (message === 'free_time_minutes must be a whole number of minutes between 0 and 1440') {
        return { key: 'settings.freeTime.range', field: 'free_time_minutes' };
      }
      // owner-fees (0097): truck_lease_create on a carrier's own truck; generate on a week not started.
      if (/^a carrier's own truck is not leased on/.test(message)) return { key: 'ownerFees.errors.ownTruck' };
      if (/^week_start must (be a Monday|not be after this week)/.test(message)) return { key: 'ownerFees.errors.weekNotMonday', field: 'week_start' };
      const fees = feeOverrideParameter(message);
      if (fees) return fees;
      const credit = creditParameter(message);
      if (credit) return credit;
      const ledger = ledgerParameter(message);
      if (ledger) return ledger;
      const load = loadParameter(message);
      if (load) return load;
      const first = message.split(/\s/, 1)[0];
      if (ALL_FIELD_KEYS.has(first)) {
        return { key: 'errors.invalidField', keyValues: { field: fieldLabelKey(first) }, field: first };
      }
      return { key: 'errors.invalid' };
    }
    case '23505': {
      const hit = DUPLICATE_PATTERNS.find((p) => p.message === message);
      if (hit) return { key: hit.key, field: hit.field };
      // A raw unique-index violation (load_create does not catch it, F14).
      if (message.includes('"loads_agency_number_key"')) return { key: 'errors.duplicateLoadNumber', field: 'load_number' };
      // load_update (0079) catches it and names it.
      if (message === 'a load with this number already exists') return { key: 'errors.duplicateLoadNumber', field: 'load_number' };
      // broker_receivable_mark_invoiced / _update (0072): the number is unique per carrier.
      const inv = /^this carrier already used invoice number (.+)$/.exec(message);
      if (inv) return { key: 'errors.duplicateInvoiceNumber', values: { number: inv[1] }, field: 'invoice_number' };
      return { key: 'errors.duplicate' };
    }
    case '55000': {
      const blocked = dispatchBlocked(message);
      if (blocked) return blocked;
      const m = NOT_ACTIVE_RE.exec(message);
      if (m) return { key: 'errors.notActive', keyValues: { thing: `entities.${m[1]}` } };
      const moved = loadMoved(message);
      if (moved) return moved;
      return {
        key:
          findPattern(STATE_PATTERNS, message) ??
          findPattern(LOAD_STATE_PATTERNS, message) ??
          findPattern(OWNER_FEE_STATE_PATTERNS, message) ??
          findPattern(CREDIT_STATE_PATTERNS, message) ??
          'errors.badState',
      };
    }
    case '23514':
      return { key: 'errors.constraint' };
    case 'CWI05':
      return { key: 'errors.driverLinked' };
    case 'CW409':
      return { key: 'errors.testFence' };
    case 'CWD01':
      // driver_create / driver_update: the agency already has a driver with this phone (driver-duplicate-phone).
      return { key: 'errors.driverDuplicatePhone', field: 'phone' };
    case 'CWP01':
    case 'CWP02': {
      // broker_receivable_mark_invoiced / broker_invoice_email (audit-b-docs, 0313): a required load document is
      // missing. CWP01 = warn (the dialog arms "Mark invoiced anyway"); CWP02 = the agency blocks it.
      const kinds = parseMissingKinds(message);
      const base = code === 'CWP01' ? 'errors.invoicePackageIncomplete' : 'errors.invoicePackageBlocked';
      return kinds.length > 0
        ? { key: base, keyLists: { docs: kinds.map((k) => `receivables.docs.kinds.${k}`) } }
        : { key: `${base}Generic` };
    }
    case 'CWT01':
      // legal_accept: the version in force moved while the user was reading (terms-acceptance).
      return { key: 'errors.termsChanged' };
    case 'CWB01': {
      // truck_create / truck_set_status / carrier_set_status (billing-truck-cap, 0175): the truck would take
      // a standard agency past its plan. The cap is in the message; the operator can move it, so no constant.
      const cap = /up to (\d+) active trucks/.exec(message);
      return cap ? { key: 'errors.truckCap', values: { cap: cap[1] } } : { key: 'errors.truckCapNoNumber' };
    }
    case '40001':
    case '40P01':
      return { key: 'errors.retry' };
    case 'PGRST202':
      return { key: 'errors.outdated' };
    default:
      return { key: 'errors.generic', values: { code: code ?? '' } };
  }
}

/** The kinds of an incomplete invoice package (0313: 'invoice package incomplete: rate_confirmation, bol'). */
export function parseMissingKinds(message: string): string[] {
  const m = /incomplete: (.+)$/.exec(message);
  return m ? m[1]!.split(',').map((k) => k.trim()).filter(Boolean) : [];
}

/** A translator for RPC errors in the current UI language. */
export function useRpcErrorMessage(): (err: RpcError) => string {
  const { t } = useTranslation();
  return React.useCallback(
    (err: RpcError) => {
      console.warn('rpc error', err.code, err.message);
      const d = describeRpcError(err);
      const translated: Record<string, string> = { ...(d.values ?? {}) };
      for (const [k, key] of Object.entries(d.keyValues ?? {})) translated[k] = t(key);
      for (const [k, keys] of Object.entries(d.keyLists ?? {})) translated[k] = keys.map((key) => t(key)).join(', ');
      const reason = t(d.key, translated);
      return d.stop === undefined ? reason : t('errors.onStop', { n: String(d.stop), reason });
    },
    [t],
  );
}
